import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fieldCls = "w-full bg-[#0B132B] border border-[#1E293B] rounded-md px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#F97316] transition-colors";
const empty = { title: "", excerpt: "", content: "", cover_image: "", published: true };

export default function BlogsTab() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const loadAll = async () => {
    try {
      const r = await api.get("/admin/blogs");
      setPosts(r.data);
    } catch { setPosts([]); }
  };
  useEffect(() => { loadAll(); }, []);

  const open = (p) => {
    setEditing(p ? p.id : "new");
    setForm(p ? { title: p.title, excerpt: p.excerpt, content: p.content || "", cover_image: p.cover_image || "", published: p.published } : empty);
  };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/blogs", form);
      else await api.put(`/admin/blogs/${editing}`, form);
      toast.success("Post saved");
      setEditing(null);
      loadAll();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-6" data-testid="blogs-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold text-white">Blog Posts ({posts.length})</h2>
        <button onClick={() => open(null)} data-testid="add-post-btn"
          className="flex items-center gap-1.5 text-xs bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold rounded-md px-3 py-2 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Post
        </button>
      </div>

      {editing && (
        <div className="bg-[#131C38] border border-[#F97316]/40 rounded-lg p-5 mb-6" data-testid="blog-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white">{editing === "new" ? "New Post" : "Edit Post"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          <div className="space-y-3">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Post title" className={fieldCls} data-testid="blog-title-input" />
            <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Short excerpt (shown on cards)" className={fieldCls} data-testid="blog-excerpt-input" />
            <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
              placeholder="Cover image URL" className={fieldCls} data-testid="blog-cover-input" />
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Article content — blank line between paragraphs" rows={10} className={fieldCls} data-testid="blog-content-input" />
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={form.published} data-testid="blog-published-toggle"
                onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[#F97316] w-4 h-4" /> Published
            </label>
            <button onClick={save} data-testid="blog-save-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-[#0B132B] font-bold text-xs px-5 py-2.5 rounded-md transition-colors">Save Post</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-[#131C38] border border-[#1E293B] rounded-lg p-4 flex items-center gap-4" data-testid={`admin-post-${p.slug}`}>
            {p.cover_image && <img src={p.cover_image} alt="" className="w-16 h-12 object-cover rounded border border-[#1E293B] hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{p.title}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {p.published ? <span className="text-emerald-400">Published</span> : <span className="text-slate-500">Draft</span>}
                {" · "}{new Date(p.created_at).toLocaleDateString()}
              </div>
            </div>
            <button onClick={() => open(p)} data-testid={`edit-post-${p.slug}`} className="p-2 text-slate-400 hover:text-[#F97316]"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} data-testid={`delete-post-${p.slug}`} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="text-sm text-slate-500" data-testid="blogs-empty">No posts yet.</p>}
      </div>
    </div>
  );
}
